import React from 'react';
import { slugify } from "../../../utils";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

const BlogList = ({ data, StyleVar = '' }) => {

    const postId = data.id;
    const title = data.title?.rendered || data.title || 'Untitled';
    const author = data.author || 'Unknown Author';
    const date = data.date || new Date().toLocaleDateString();
    const featuredImage = data.featured_image || data.image || '';
    const link = data.link || '#';
    let categoryLinks = [];
    
    if (data.categories && Array.isArray(data.categories)) {
        if (data._embedded && data._embedded['wp:term'] && data._embedded['wp:term'][0]) {
            const categoryTerms = data._embedded['wp:term'][0];
            categoryLinks = categoryTerms.map((category, i) => (
                <Link 
                    to={process.env.PUBLIC_URL + `/category/${slugify(category.name)}`} 
                    key={category.id}
                >
                    {category.name}
                    {i !== categoryTerms.length - 1 && ", "}
                </Link>
            ));
        } else {
            categoryLinks = data.categories.map((categoryId, i) => (
                <Link 
                    to={process.env.PUBLIC_URL + `/category/${categoryId}`} 
                    key={categoryId}
                >
                    Category {categoryId}
                    {i !== data.categories.length - 1 && ", "}
                </Link>
            ));
        }
    } else if (typeof data.categories === 'string') {
        const categoriesArray = data.categories.split(',').map(cat => cat.trim());
        categoryLinks = categoriesArray.map((category, i) => (
            <Link 
                to={process.env.PUBLIC_URL + `/category/${slugify(category)}`} 
                key={i}
            >
                {category}
                {i !== categoriesArray.length - 1 && ", "}
            </Link>
        ));
    }
    const handleImageError = (e) => {
        e.target.src = process.env.PUBLIC_URL + '/images/placeholder-blog.jpg';
        e.target.alt = 'Placeholder image';
        e.target.onerror = () => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
        };
    };
    const isExternalPost = data.link && data.link.includes('whatson.guide');
    
    return (
        <div className={`rn-card ${StyleVar}`}>
            <div className="inner">
                <div className="thumbnail">
                    {isExternalPost ? (
                        <a 
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="image"
                            aria-label={`Read external post: ${title}`}
                            style={{ position: 'relative', display: 'block' }}
                        >
                            {featuredImage ? (
                                <div 
                                    style={{
                                        width: '100%',
                                        height: '200px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        padding: '8px',
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    <img 
                                        src={featuredImage} 
                                        alt={title}
                                        onError={handleImageError}
                                        loading="lazy"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain',
                                            borderRadius: '4px',
                                            transition: 'transform 0.3s ease'
                                        }}
                                        onMouseOver={(e) => e.target.style.transform = 'scale(1.02)'}
                                        onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                                    />
                                    <div 
                                        style={{
                                            display: 'none',
                                            width: '100%',
                                            height: '100%',
                                            backgroundColor: '#f0f0f0',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#666',
                                            fontSize: '14px'
                                        }}
                                    >
                                        No Image Available
                                    </div>
                                </div>
                            ) : (
                                <div 
                                    style={{
                                        width: '100%',
                                        height: '200px',
                                        backgroundColor: '#f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '8px',
                                        color: '#666',
                                        fontSize: '14px'
                                    }}
                                >
                                    No Image Available
                                </div>
                            )}
                            {/* External link indicator */}
                            <div style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                background: 'rgba(0,0,0,0.8)',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600'
                            }}>
                                External
                            </div>
                        </a>
                    ) : (
                        // Internal post - use React Router Link
                        <Link 
                            to={process.env.PUBLIC_URL + `/blog-details/${postId}`} 
                            className="image"
                            style={{ display: 'block' }}
                        >
                            <div 
                                style={{
                                    width: '100%',
                                    height: '200px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    padding: '8px',
                                    boxSizing: 'border-box'
                                }}
                            >
                                <img 
                                    src={featuredImage.startsWith('http') ? featuredImage : `${process.env.PUBLIC_URL}/${featuredImage}`} 
                                    alt={title}
                                    onError={handleImageError}
                                    loading="lazy"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain',
                                        borderRadius: '4px',
                                        transition: 'transform 0.3s ease'
                                    }}
                                    onMouseOver={(e) => e.target.style.transform = 'scale(1.02)'}
                                    onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                                />
                            </div>
                        </Link>
                    )}
                </div>
                
                <div className="content">
                    <ul className="rn-meta-list" role="list">
                        <li>
                            {isExternalPost ? (
                                <span style={{ color: '#666' }}>{author}</span>
                            ) : (
                                <Link 
                                    to={process.env.PUBLIC_URL + `/archive/${slugify(author)}`}
                                    style={{ textDecoration: 'none' }}
                                    aria-label={`View all posts by ${author}`}
                                >
                                    {author}
                                </Link>
                            )}
                        </li>
                        <li className="separator" aria-hidden="true">/</li>
                        <li style={{ color: '#888' }}>{date}</li>
                        {categoryLinks.length > 0 && (
                            <>
                                <li className="separator" aria-hidden="true">/</li>
                                <li>{categoryLinks}</li>
                            </>
                        )}
                    </ul>
                    
                    <h4 className="title">
                        {isExternalPost ? (
                            <a 
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ 
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    lineHeight: '1.4'
                                }}
                            >
                                {title}
                            </a>
                        ) : (
                            <Link 
                                to={process.env.PUBLIC_URL + `/blog-details/${postId}`}
                                style={{ 
                                    textDecoration: 'none',
                                    color: 'inherit'
                                }}
                            >
                                {title}
                            </Link>
                        )}
                    </h4>
                    
                    {/* Show excerpt if available */}
                    {data.excerpt?.rendered && (
                        <p 
                            className="description" 
                            style={{ 
                                fontSize: '14px', 
                                color: '#666',
                                marginTop: '10px',
                                lineHeight: '1.6',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                            }}
                        >
                            {data.excerpt.rendered.replace(/<[^>]*>/g, '')}
                        </p>
                    )}
                    
                    {/* External post indicator */}
                    {isExternalPost && (
                        <div style={{ 
                            marginTop: '10px',
                            fontSize: '12px',
                            color: '#007cba',
                            fontWeight: '500',
                            padding: '5px 10px',
                            backgroundColor: '#f0f8ff',
                            borderRadius: '4px',
                            display: 'inline-block'
                        }}>
                            📖 Read full article on whatson.guide
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

BlogList.propTypes = {
    data: PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                rendered: PropTypes.string
            })
        ]),
        author: PropTypes.string,
        date: PropTypes.string,
        categories: PropTypes.oneOfType([
            PropTypes.array,
            PropTypes.string
        ]),
        image: PropTypes.string,
        featured_image: PropTypes.string,
        link: PropTypes.string,
        excerpt: PropTypes.shape({
            rendered: PropTypes.string
        }),
        _embedded: PropTypes.object
    }).isRequired,
    StyleVar: PropTypes.string
};

export default BlogList;
